import { z } from 'zod';

/**
 * Enter Tasks — shared domain types.
 *
 * A Task is an OPEN Enter "provider message" assigned to the current user
 * (Enter's Worker system does the assignment). The gateway maps upstream
 * `ProviderMessage` → `EnterTask`, keeping Enter API churn behind the service
 * seam. Full contract + verification notes: docs/ENTER_TASKS_INTEGRATION.md.
 */

/** Severity dot — mapped from Enter `MessagePriority` (URGENT|HIGH|NORMAL|LOW). */
export const EnterTaskSeveritySchema = z.enum(['urgent', 'high', 'normal', 'low']);
export type EnterTaskSeverity = z.infer<typeof EnterTaskSeveritySchema>;

export const EnterTaskSchema = z.object({
  /** ProviderMessage.id */
  id: z.string(),
  /** Enter account (practice) the task belongs to — routes the close call back to the right credential. */
  accountId: z.string(),
  /** ProviderMessage.subject */
  title: z.string(),
  /** From priority; null upstream → 'normal'. */
  severity: EnterTaskSeveritySchema,
  open: z.boolean(),
  dueDate: z.string().optional(),
  /** First links.claims[].id — the "located in" reference. */
  claimId: z.string().optional(),
  /** First links.patients[].id. */
  patientId: z.string().optional(),
  /**
   * The task's descriptive text. Enter's ProviderMessage has NO `body` field —
   * the text rendered under the subject is the FIRST comment ("root note"), so
   * that's what this carries. Absent when the message has no comments.
   */
  body: z.string().optional(),
  /** Message author's display name (`author.nickname`). */
  authorName: z.string().optional(),
  /** ISO 8601 creation timestamp. */
  createdAt: z.string().optional(),
  commentCount: z.number(),
  updatedAt: z.string().optional(),
  // ── Optional enrichment for the patient-snapshot cards. The user-scoped
  //    sidebar worklist leaves these unset; the patient-scoped list resolves
  //    them from the Enter roster + category catalog (id → name). ──
  /**
   * First `links.users[].id` (= personalAccountId). ABSENT means the task is
   * genuinely UNASSIGNED — distinct from "assigned to someone whose display
   * name we couldn't resolve", which `assigneeName` alone can't express.
   */
  assigneeId: z.string().optional(),
  /** Resolved assignee display name — links.users[0].id → /provider/user first+last. */
  assigneeName: z.string().optional(),
  /** Resolved category label — links.messageCategories[0].id → /provider/message_category name. */
  categoryName: z.string().optional(),
  /**
   * ALL resolved category labels, not just the first.
   *
   * Platform's Tasks UI decides real behaviour from the full set — whether the
   * task can be closed at all (`Claim Halted` cannot), and which resolve
   * options apply. Reading only `categoryName` would silently mis-handle any
   * message carrying more than one category, so this is the field to branch on;
   * `categoryName` stays for the existing single-label chip.
   */
  categoryNames: z.array(z.string()).default([]),
});
export type EnterTask = z.infer<typeof EnterTaskSchema>;

export const ListTasksOutputSchema = z.object({
  tasks: z.array(EnterTaskSchema),
  /** 'none' → org has no connected Enter integration; sidebar renders the "Connect Enter" CTA. */
  integration: z.enum(['connected', 'none']),
});
export type ListTasksOutput = z.infer<typeof ListTasksOutputSchema>;

export const CloseTaskInputSchema = z.object({
  /** The account the task came from (echoed back from the EnterTask). */
  accountId: z.string(),
  /** Optional resolution note posted as a comment before closing. */
  note: z.string().optional(),
});
export type CloseTaskInput = z.infer<typeof CloseTaskInputSchema>;

export const PostTaskCommentInputSchema = z.object({
  /** The account the task came from (echoed back from the EnterTask). */
  accountId: z.string(),
  /**
   * Comment body. For a Resolve action this is a SENTINEL string that Enter's
   * backend matches on (see `enter-task-reason.ts`) — not free prose, and not
   * something to trim or normalise on the way through.
   */
  body: z.string().min(1),
});
export type PostTaskCommentInput = z.infer<typeof PostTaskCommentInputSchema>;

/**
 * A comment on a task. We read these for ONE reason: to tell whether a resolve
 * action already happened. Platform derives its "Resolved" card from the thread
 * (`getResolveSuccessKind`), which is what makes the state survive a reload —
 * local component state forgets the moment the panel closes, and a task would
 * then offer to resolve something already resolved.
 */
export const TaskCommentSchema = z.object({
  id: z.string(),
  body: z.string().optional(),
  /** ISO 8601. Also what orders the thread — array order is not a promise. */
  created: z.string().optional(),
  authorName: z.string().optional(),
});
export type TaskComment = z.infer<typeof TaskCommentSchema>;

/**
 * Patient + claim identity for a task, fetched on demand when a task is opened.
 *
 * WHY THIS EXISTS (Jordan, 2026-08-21): "the most important thing to show is the
 * MRN — this is what the user needs to be able to find the record in the EMR to
 * fix the task." Until now the panel showed a truncated Enter claim id, which is
 * an internal guid and useless for locating anything in an EMR.
 *
 * `mrn` is Enter's `ProviderClaim.patientExternalId` — documented upstream as
 * "The patient id from the external system", i.e. the EMR's own identifier. That
 * is precisely the join key a biller needs, and it is why it matters more than
 * the name.
 *
 * Everything here comes from ONE `GET /provider/claim/{id}`. Platform assembles
 * the same card from the latest HL7 message plus a separate payer lookup; the
 * claim model already carries all of it, so we take the cheaper route.
 */
export const ClaimSummarySchema = z.object({
  claimId: z.string(),
  /** `patientExternalId` — the EMR's patient id. THE field Jordan asked for. */
  mrn: z.string().optional(),
  patientName: z.string().optional(),
  /** ISO date. */
  patientDob: z.string().optional(),
  /** From-date of service. */
  fromDos: z.string().optional(),
  payerName: z.string().optional(),
  /** Dollars. Absent when upstream has no amount — never defaulted to 0, which
   *  would read as "this claim is worth nothing" rather than "unknown". */
  chargeAmount: z.number().optional(),
  /** Visit account number — a second lookup key in some EMRs. */
  accountNumber: z.string().optional(),
});
export type ClaimSummary = z.infer<typeof ClaimSummarySchema>;
